import { Controller, useForm, type Resolver } from "react-hook-form";
import { z } from "zod";
import { Alert, Modal, Select, TextArea, type SelectOption } from "@/deps/ui-library";
import { useAccounts, useReopenYear } from "@/api/queries";
import type { YearEndPreview } from "@/api/types";
import { formatDate } from "@/lib/dates";

const schema = z.object({
  periodIds: z.array(z.string()).min(1, "Choose at least one month."),
  accountIds: z.array(z.string()).min(1, "Choose at least one account."),
  reason: z.string().trim().min(1, "Say why the year is being reopened."),
});
type Values = z.infer<typeof schema>;

/** The one check this form has, as react-hook-form asks for it: each problem on its own field. */
const resolver: Resolver<Values> = (values) => {
  const parsed = schema.safeParse(values);
  if (parsed.success) return { values: parsed.data, errors: {} };

  const errors: Record<string, { type: string; message: string }> = {};
  for (const issue of parsed.error.issues) {
    errors[String(issue.path[0])] ??= { type: issue.code, message: issue.message };
  }
  return { values: {}, errors };
};

const chosen = (value: SelectOption | SelectOption[] | null) =>
  (Array.isArray(value) ? value : value ? [value] : []).map((option) => String(option.value));

/**
 * Reopening a closed year (ADR-0058): an administrator says why, which months open and which accounts
 * may post. The rest of the year stays closed, and it closes again as its next version.
 */
export function ReopenYearDialog({
  open,
  onClose,
  preview,
}: {
  open: boolean;
  onClose: () => void;
  preview: YearEndPreview;
}) {
  const { data: accounts } = useAccounts();
  const reopen = useReopenYear();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver,
    defaultValues: { periodIds: [], accountIds: [], reason: "" },
  });

  const dismiss = () => {
    reset();
    reopen.reset();
    onClose();
  };

  return (
    <Modal.Form
      open={open}
      title={`Reopen ${preview.fiscalYearCode}?`}
      subtitle="Only the months and accounts you choose can take postings, until the year closes again."
      submitLabel="Reopen the year"
      busy={reopen.isPending}
      error={reopen.error ? (reopen.error as Error).message : null}
      onClose={dismiss}
      onSubmit={() =>
        void handleSubmit((values) =>
          reopen.mutate({ fiscalYearId: preview.fiscalYearId, ...values }, { onSuccess: dismiss }),
        )()
      }
    >
      <div className="flex flex-col gap-3">
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          description="A return already filed for these months won't change by itself. If a correction changes what was filed, amend the return with the IRD."
        />
        <Controller
          control={control}
          name="periodIds"
          render={({ field }) => (
            <Select
              multiple
              required
              label="Months to reopen"
              placeholder="Choose months"
              options={(preview.periods ?? []).map((period) => ({
                value: period.periodId,
                label: `${formatDate(period.startsOn)} – ${formatDate(period.endsOn)}`,
              }))}
              value={field.value}
              errorText={errors.periodIds?.message}
              onChange={(details) => field.onChange(chosen(details.value))}
            />
          )}
        />
        <Controller
          control={control}
          name="accountIds"
          render={({ field }) => (
            <Select
              multiple
              required
              searchable
              label="Accounts that may post"
              placeholder="Choose accounts"
              options={(accounts ?? [])
                .filter((account) => account.isActive)
                .map((account) => ({
                  value: account.id,
                  label: `${account.code} · ${account.name}`,
                }))}
              value={field.value}
              errorText={errors.accountIds?.message}
              helperText="A posting to any other account will be refused, so include both sides of what you plan to post."
              onChange={(details) => field.onChange(chosen(details.value))}
            />
          )}
        />
        <Controller
          control={control}
          name="reason"
          render={({ field }) => (
            <TextArea
              required
              label="Reason"
              rows={2}
              value={field.value}
              errorText={errors.reason?.message}
              onChange={field.onChange}
            />
          )}
        />
      </div>
    </Modal.Form>
  );
}
